import React from "react";
import { ReactElement, useReducer, useMemo } from "react";
import { Some } from "@swiggy-private/js-fp";
import {
    useUserInfo,
    UserContext,
    SwgyHeadersKeys,
    CheckoutContext,
    ApiContext,
    ApiContextValue,
    HardwareBackContext,
    ConfigContext,
    ConfigContextValue,
    AnalyticsContext,
    CheckoutState,
    reducer as checkoutReducer,
    ConfirmOrderContext,
    useCheckoutWebDynamicConfig,
    CheckoutWebDynamicConfigContext,
    useAudioPlayer,
    AudioPlayerContext,
    PublicApiProvider,
    BackNavigationContextProvider,
} from "@swiggy-private/checkout-core";
import { EmiSelectionProvider } from "@payment-client/components/emi-selection/EmiSelectionProvider";
import Analytics, { DEFAULT_ANALYTICS_CONTEXT_VALUE } from "@swiggy-private/analytics";
import { getAnalyticsConfigParams } from "@swiggy-private/checkout-core";
import { useState } from "react";
import { GlobalStore } from "@swiggy-private/core-ui";
import { PaymentMainProps } from "@payment-client/components/payment-main";
import { OrderApiData, ApiResponse } from "@swiggy-private/payment-types";
import { PublicApiGlobalState } from "@swiggy-private/core-utils";
export interface ContextProviderProps {
    config: {
        apiHost: string;
        paymentHost: string;
        analyticsHost: string;
        paymentReturnUrl?: string;
        addMoneyReturnUrl?: string;
        addSwiggyMoneyReturnUrl?: string;
        cachePaymentData?: boolean;
        enableCardRateLimit?: boolean;
        enablePublicApi?: boolean;
        simplPostLinkingRedirectUrl: string;
        enableConfirmOrderDifferentialOrderIdAlert?: boolean;
        isJuspayV3Enabled?: boolean;
    };
    headers: { [key in Partial<SwgyHeadersKeys>]: string };
    initialState: CheckoutState;
    children: ReactElement;
    turboPayConfig?: PaymentMainProps["turboPayConfig"];
    turboPayPlaceOrderData?: PaymentMainProps["turboPayPlaceOrderData"];
}

const updateApiContextValue = (apiHost: string, paymentHost: string): ApiContextValue => ({
    host: Some(apiHost),
    paymentHost: Some(paymentHost),
});
const updateConfigContextValue = (
    config: ContextProviderProps["config"],
    turboPayConfig?: ContextProviderProps["turboPayConfig"],
    turboPayPlaceOrderData?: ContextProviderProps["turboPayPlaceOrderData"],
): ConfigContextValue => ({
    host: Some(config.apiHost),
    analyticsHost: Some(config.analyticsHost),
    paymentReturnUrl: Some(config.paymentReturnUrl),
    addMoneyReturnUrl: Some(config.addMoneyReturnUrl),
    addSwiggyMoneyReturnUrl: Some(config.addSwiggyMoneyReturnUrl),
    cachePaymentData: Some(config.cachePaymentData),
    turboPayConfig: Some(turboPayConfig),
    turboPayPlaceOrderData: turboPayPlaceOrderData,
    enableCardRateLimit: Some(config.enableCardRateLimit),
    enablePublicApi: Some(config.enablePublicApi),
    simplPostLinkingRedirectUrl: Some(config.simplPostLinkingRedirectUrl),
    enableConfirmOrderDifferentialOrderIdAlert: Some(config.enableConfirmOrderDifferentialOrderIdAlert || false),
    isJuspayV3Enabled: Some(config.isJuspayV3Enabled),
});

export function ContextProvider(props: ContextProviderProps): ReactElement {
    const { config, turboPayConfig, initialState, children, turboPayPlaceOrderData } = props;
    const [user, setUser] = useUserInfo();
    const checkoutWebDynamicConfigValue = useCheckoutWebDynamicConfig();

    const [checkoutState, setCheckoutState] = useReducer(checkoutReducer, initialState);
    const [hardwareBackState, setHardwareBackState] = useState({ shouldHandle: false });
    const [confirmOrderContextState, setConfirmOrderContextState] = useState<ApiResponse<OrderApiData>>();
    const apiContextValue = useMemo(
        () => updateApiContextValue(config.apiHost, config.paymentHost),
        [config.apiHost, config.paymentHost],
    );

    PublicApiGlobalState.setEnablePublicApi(typeof config?.enablePublicApi == "undefined" || config?.enablePublicApi);

    const configContextValue = useMemo(
        () => updateConfigContextValue(config, turboPayConfig, turboPayPlaceOrderData),
        [config, turboPayConfig, turboPayPlaceOrderData],
    );
    const checkoutContextValue = useMemo(
        () => ({
            checkoutState,
            setCheckoutState,
        }),
        [checkoutState],
    );
    const analyticsContextValue = useMemo(() => {
        const analyticsConfig = getAnalyticsConfigParams(user, config.analyticsHost);
        const analytics = (analyticsConfig && new Analytics(analyticsConfig)) || DEFAULT_ANALYTICS_CONTEXT_VALUE;
        return analytics;
    }, [config.analyticsHost, user]);

    const hardwareBackContextValue = useMemo(() => {
        return {
            state: hardwareBackState,
            setState: setHardwareBackState,
        };
    }, [hardwareBackState]);

    const userContextValue = useMemo(() => {
        return {
            user,
            setUser,
        };
    }, [user, setUser]);

    const confirmOrderContextValue = useMemo(() => {
        return {
            confirmOrderContext: confirmOrderContextState,
            setConfirmOrderContext: setConfirmOrderContextState,
        };
    }, [confirmOrderContextState]);

    const audioPlayerContextValue = useAudioPlayer();

    // payment
    return (
        <AnalyticsContext.Provider value={analyticsContextValue}>
            <CheckoutWebDynamicConfigContext.Provider value={checkoutWebDynamicConfigValue}>
                <CheckoutContext.Provider value={checkoutContextValue}>
                    <UserContext.Provider value={userContextValue}>
                        <ConfigContext.Provider value={configContextValue}>
                            <PublicApiProvider>
                                <HardwareBackContext.Provider value={hardwareBackContextValue}>
                                    <ApiContext.Provider value={apiContextValue}>
                                        <ConfirmOrderContext.Provider value={confirmOrderContextValue}>
                                            <AudioPlayerContext.Provider value={audioPlayerContextValue}>
                                                <BackNavigationContextProvider>
                                                    <EmiSelectionProvider>
                                                        <GlobalStore>{children}</GlobalStore>
                                                    </EmiSelectionProvider>
                                                </BackNavigationContextProvider>
                                            </AudioPlayerContext.Provider>
                                        </ConfirmOrderContext.Provider>
                                    </ApiContext.Provider>
                                </HardwareBackContext.Provider>
                            </PublicApiProvider>
                        </ConfigContext.Provider>
                    </UserContext.Provider>
                </CheckoutContext.Provider>
            </CheckoutWebDynamicConfigContext.Provider>
        </AnalyticsContext.Provider>
    );
}
